import { useEffect, useState } from "react";
import useUOM from "src/shared/hooks/useUOM";
import { CategoryV2, Product } from "src/shared/services/interfacev2";
import { compressImage } from "./compressImage";
import { notifyWarning } from "src/assets/toastService";

// ─── Shared form shape ────────────────────────────────────────────────────────
export type ProductFormState = {
  productName: string;
  arabicName: string;
  amount: string;
  type: string;
  isService: boolean;
  categoryId: string;
  categoryName: string;
  image: File | string | null;
  imagePreview: string;
  barcodeId: string;
  uom: { id: string; uomName: string }[];
  productRefId: string;
  amountMeasurement: string;
  purchasePrice: number;
  wholeSalePrice: number;
  sellingPrice: number;
  discount: number;
  discountType: boolean;
};

export const EMPTY_FORM: ProductFormState = {
  productName: "",
  arabicName: "",
  amount: "",
  type: "",
  isService: false,
  categoryId: "",
  categoryName: "",
  image: null,
  imagePreview: "",
  barcodeId: "",
  uom: [],
  productRefId: "",
  amountMeasurement: "",
  purchasePrice: 0,
  sellingPrice: 0,
  wholeSalePrice: 0,
  discount: 0,
  discountType: false,
};

// Map a Product → ProductFormState (used by EditProducts)
export function productToFormState(p: Product): ProductFormState {
  const discountRaw = String(p.discount ?? "0");
  const isPercentage = discountRaw.includes("%");
  return {
    productName: p.productName || "",
    arabicName: p.arabicName || "",
    amount: p.amount || "",
    type: p.type || "",
    isService: p.isService || false,
    categoryId: p.categoryId || "",
    categoryName: p.categoryName || "",
    image: p.imageUrl || null,
    imagePreview: p.imageUrl || "",
    barcodeId: p.barcodeId || "",
    uom: p.unitConfiguration.uom.map((u) => ({ id: u.id, uomName: u.uomName })),
    productRefId: p.productRefId || "",
    amountMeasurement: p.amountMeasurement || "",
    purchasePrice: Number(p.purchasePrice) || 0,
    sellingPrice: Number(p.sellingPrice) || 0,
    discount: Number(discountRaw.replace("%", "")) || 0,
    discountType: isPercentage,
    wholeSalePrice: Number(p.wholeSalePrice) || 0,
  };
}

type Options = {
  category: CategoryV2[];
  initialState: ProductFormState;
};

export function useProductForm({ category, initialState }: Options) {
  const { uom } = useUOM();
  const [form, setForm] = useState<ProductFormState>(initialState);
  const [productType, setProductType] = useState<"Inward" | "Service">(initialState.isService ? "Service" : "Inward");
  const [barcodeMode, setBarcodeMode] = useState(!!initialState.barcodeId);
  const [scanMode, setScanMode] = useState(false);
  const [submit, setSubmit] = useState(false);

  // ── Auto-fill category name ──────────────────────────────────────────────
  useEffect(() => {
    const selected = category.find((c) => c._id.$oid === form.categoryId);
    if (selected) setForm((prev) => ({ ...prev, categoryName: selected.name }));
  }, [form.categoryId, category]);

  // ── Clear amount/measurement when UOM removed ────────────────────────────
  useEffect(() => {
    if (form.uom.length === 0) {
      setForm((prev) => ({ ...prev, amount: "", amountMeasurement: "" }));
    }
  }, [form.uom]);

  // ── Price / discount validation (debounced) ──────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      const { discountType, discount, sellingPrice, purchasePrice } = form;
      const d = Number(discount);
      const sp = Number(sellingPrice);
      const pp = Number(purchasePrice);

      if (discountType && d > 99) {
        notifyWarning("Percentage discount cannot be more than 99%");
        setForm((prev) => ({ ...prev, discount: 0 }));
        return;
      }
      if (sp === 0 && pp === 0) return;
      if (!discountType && d >= sp) {
        notifyWarning("Discount amount must be less than the selling price");
        return;
      }
      if (sp <= pp) {
        notifyWarning("Selling price must be greater than the purchase price");
        return;
      }
      if (!discountType && pp >= sp - d) {
        notifyWarning("Discount price must be greater than the purchase price");
        return;
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.discount, form.discountType, form.sellingPrice, form.purchasePrice]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImage(file, (compressed, preview) => setForm((prev) => ({ ...prev, image: compressed, imagePreview: preview })));
  };

  const handleBarcodeDetected = (code: string) => {
    setForm((prev) => ({ ...prev, barcodeId: code }));
    setScanMode(false);
    setBarcodeMode(true);
  };

  const setField = <K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const switchProductType = (type: "Inward" | "Service") => {
    setProductType(type);
    setField("isService", type === "Service");
  };

  // ── Derived price values ─────────────────────────────────────────────────
  const sp = Number(form.sellingPrice) || 0;
  const pp = Number(form.purchasePrice) || 0;
  const d = Number(form.discount) || 0;
  const discountPrice = form.discountType ? (sp * d) / 100 : d;
  const finalPrice = sp - discountPrice;
  const discountString = form.discount + (form.discountType ? "%" : "");

  return {
    form,
    setForm,
    setField,
    uom,
    productType,
    switchProductType,
    barcodeMode,
    setBarcodeMode,
    scanMode,
    setScanMode,
    submit,
    setSubmit,
    handleImageChange,
    handleBarcodeDetected,
    // price
    discountPrice,
    finalPrice,
    discountString,
    sellingPrice: sp,
    purchasePrice: pp,
    wholeSalePrice: Number(form.wholeSalePrice) || 0,
  };
}
